import PropTypes from 'prop-types';
import { useContext, useEffect, useMemo, useState } from 'react';

import { forEach, includes } from 'lib/helpers';
import _each from 'lodash/each';
import _uniqBy from 'lodash/uniqBy';

import Cookies from 'js-cookie';

import styled from 'styled-components';
import colors from 'styles/colors';
import fonts from 'styles/fonts';
import media from 'styles/media';

import { CruiseSearchContext } from 'contexts/CruiseSearchContext';

import { getObjectFromQuery, validateUniqueAppliedUtil } from 'contexts/CruiseSearchContext/utils/uniqueDestinationsUtils';

import PillButton from 'components/buttons/PillButton';

import { CruiseSelectionContext } from 'contexts/CruiseSelectionContext';
import { SELECTING } from 'contexts/CruiseSelectionContext/constants';

import {
	ADULTS,
	CABIN_CLASS,
	CHILDREN,
	COUPON,
	CUSTOM,
	DEPARTURE_PORT,
	DESTINATION,
	EMAIL_ID,
	LOYALTY_ID,
	NIGHTS,
	NUMBER_OF_GUESTS,
	OCCUPANCY,
	PORT,
	QUALIFIERS,
	SHIP,
	START_DATE,
} from 'contexts/CruiseSearchContext/constants';
import sizes from 'styles/sizes';
import { RESET_SELECTED_FILTERS, SELECTED_FILTER, SELECTED_FILTERS } from './constants';
import { findFilterLabelFromCode, formatStartDate, makeCabinClassLabel, makeOcupancyLabel, makeQualifierLabel, makeRangeLabel } from './utils';

const SelectedFiltersWrapper = styled.div`
	display: block;

	&.compare {
		display: none;
	}
`;

const Wrapper = styled.div`
	max-width: 1000px;
	padding: 0 20px;
	margin: 0 auto;
	position: relative;

	display: flex;
	align-items: center;
	justify-content: center;
	flex-wrap: wrap;

	button {
		margin-bottom: 10px;
		margin-right: 8px;
		flex-shrink: 0;
	}

	${media.small`
    display: flex;
    justify-content: flex-start;
  `}
`;

const ClearAllWrapper = styled(Wrapper)`
	margin: 0 auto;
	padding: 0 20px;

	@media (min-width: ${sizes.media.smallMaxWidth}px) {
		padding: 2px 0 0;
	}
`;

const ResetButton = styled.button`
	border-bottom: 1px solid ${colors.primary.ocean};
	transition:
		color 0.1s cubic-bezier(0, 0, 0, 0),
		background-color 0.1s cubic-bezier(0, 0, 0, 0);
	color: ${colors.primary.ocean};
	font-family: ${fonts.primary.bold};
	font-size: 14px;
	letter-spacing: 0.01em;
	margin: 0;
	text-align: center;

	&:hover {
		color: ${colors.secondary.deepCoveLight};
		border-color: ${colors.secondary.deepCoveLight};
	}
`;

/**
 * Collection of PillButtons showing which search filters are currently applied
 *
 * @param {object} passedRef - Used for animations
 */

export const getParseMap = (labels) => {
	const {
		labelDeparturePortPrepender,
		labelNightsAppender,
		labelCouponPrepender,
		cabinClassOptions,
		labelTravelersFilterAdult,
		labelTravelersFilterAdults,
		labelTravelersFilterChild,
		labelTravelersFilterKids,
	} = labels;
	return {
		[START_DATE]: {
			filterName: START_DATE,
			makeLabel: formatStartDate,
		},
		[DESTINATION]: {
			filterName: DESTINATION,
			makeLabel: findFilterLabelFromCode,
		},
		[PORT]: {
			filterName: PORT,
			makeLabel: findFilterLabelFromCode,
		},
		[DEPARTURE_PORT]: {
			filterName: DEPARTURE_PORT,
			makeLabel: findFilterLabelFromCode,
			labelOptions: { prepend: labelDeparturePortPrepender }, // Where is this going to come from?
		},
		[SHIP]: {
			filterName: SHIP,
			makeLabel: findFilterLabelFromCode,
		},
		[NIGHTS]: {
			filterName: NIGHTS,
			makeLabel: makeRangeLabel,
			labelOptions: { append: labelNightsAppender }, // Where is this going to come from?
		},
		[QUALIFIERS]: {
			filterName: QUALIFIERS,
			makeLabel: makeQualifierLabel, // How is this going to work? Where are localized labels coming from?
			labelOptions: { coupon: labelCouponPrepender },
		},
		[CABIN_CLASS]: {
			filterName: CABIN_CLASS,
			makeLabel: makeCabinClassLabel,
			labelOptions: cabinClassOptions,
		},
		[ADULTS]: {
			filterName: ADULTS,
			makeLabel: makeOcupancyLabel,
			labelOptions: [labelTravelersFilterAdult, labelTravelersFilterAdults],
		},
		[CHILDREN]: {
			filterName: CHILDREN,
			makeLabel: makeOcupancyLabel,
			labelOptions: [labelTravelersFilterChild, labelTravelersFilterKids],
		},
		[CUSTOM]: {
			filterName: CUSTOM,
			makeLabel: findFilterLabelFromCode,
		},
	};
};
function SelectedFilters({ passedRef }) {
	const {
		searchApplied,
		qualifiersApplied,
		additionalParamsApplied,
		otherParamsApplied,
		removeTargetedOfferApplied,
		resetAllFilters,
		filterOptionsDictionary,
		removeFromSearchSelectingFilter,
		updateMonthsSelectingFilter,
		updateDatesSelectingFilter,
		removeFromQualifiersSelecting,
		applyFilters,
		globalization,
		uniqueDestinations,
		uniqueDestinationsAvailable,
		targetedOfferApplied,
		resetSoldOut,
		monthsSelecting,
		promotions,
		promotionApplied,
		setPromotionSelected,
		setPromotionApplied,
		updateAdditionalFilters,
		targetedOffersLoaded,
		locale,
	} = useContext(CruiseSearchContext);
	const { comparisonViewState } = useContext(CruiseSelectionContext);

	const [selectedFilters, setSelectedFilters] = useState([]);
	const [isTargetedPromo, setIsTargetedPromo] = useState(false);
	const show = !!selectedFilters.length;
	const customFiltersFromGraph = filterOptionsDictionary?.custom?.options;
	const { cabinClassOptions, filtersDictionary, generalDictionary } = globalization?.dictionaries || {};
	const { actionClearAll, labelDeparturePortPrepender, labelNightsAppender, labelCouponPrepender, labelPromoPrepender } = filtersDictionary;

	const { labelTravelersFilterAdult, labelTravelersFilterAdults, labelTravelersFilterChild, labelTravelersFilterKids } = generalDictionary;

	const mixCustomFilters = customFiltersFromGraph;

	const selectingForCompare = useMemo(() => comparisonViewState === SELECTING, [comparisonViewState]);

	const isLoyaltyOrEmail =
		[LOYALTY_ID.urlParam, EMAIL_ID.urlParam].some((item) => otherParamsApplied?.includes(item)) ||
		!!Cookies.get(LOYALTY_ID.cookie) ||
		!!Cookies.get(EMAIL_ID.cookie);

	const parseMap = getParseMap({
		labelDeparturePortPrepender,
		labelNightsAppender,
		labelCouponPrepender,
		cabinClassOptions,
		labelTravelersFilterAdult,
		labelTravelersFilterAdults,
		labelTravelersFilterChild,
		labelTravelersFilterKids,
	});

	useEffect(() => {
		/**
		 * Don't love this
		 * but want to keep search, qualifiers separate
		 * for querying/parsing purposes -KM
		 */
		const allFilters = { ...searchApplied, [QUALIFIERS]: qualifiersApplied };
		let filterUniqueDestination = null;
		let filters = null;
		let filterValue, additional, others;

		if (uniqueDestinations.length) {
			filterUniqueDestination = mixCustomFilters?.filter(
				(item) =>
					item.code ===
					uniqueDestinations.reduce((arr, value) => {
						arr.push(...Object.keys(value));

						return [...new Set(arr)];
					}, [])?.[0],
			)?.[0];

			if (filterUniqueDestination?.value) {
				[filterValue, ...additional] = filterUniqueDestination.value.split('&');
				others = getObjectFromQuery(additional.join('&'), '&', '=');
				filters = getObjectFromQuery(filterValue, '|', ':');
			}
		}

		let nextSelectedFilters = Object.keys(allFilters)
			.flatMap((filterName) =>
				allFilters[filterName].flatMap((value) => {
					const parser = parseMap[filterName];

					if (!parser) {
						return [];
					}

					let resValidUniqueFilter = false;

					if (filters?.[filterName]?.includes(value)) {
						resValidUniqueFilter = validateUniqueAppliedUtil(filterValue, { ...allFilters, additionalParamsApplied, otherParamsApplied }, others);
					}

					if (Object.keys(filterUniqueDestination || {}).length && resValidUniqueFilter) {
						return [
							{
								label: filterUniqueDestination.label,
								filterName: CUSTOM,
								value: filterUniqueDestination.code,
							},
						];
					}

					const { makeLabel, labelOptions } = parser;
					const filterOption = filterOptionsDictionary[filterName];
					let label = filterName === START_DATE ? makeLabel(value, locale) : makeLabel(value, labelOptions, filterOption, filterName);

					if (!label) {
						return [];
					}

					return [{ label, filterName, value }];
				}),
			)
			.filter(Boolean);

		if (filterUniqueDestination?.value) {
			nextSelectedFilters.push({
				label: `${labelPromoPrepender} ${promotions?.title || filterUniqueDestination.label}`,
				filterName: filterUniqueDestination.code,
				value: filterUniqueDestination.value,
				promo: true,
			});
		} else if (promotions?.valid && promotionApplied) {
			nextSelectedFilters.push({
				label: `${labelPromoPrepender} ${promotions?.title}`,
				filterName: promotions?.code,
				value: promotions?.queryCriteria,
				promo: true,
			});
		}

		const newFilters = _uniqBy(
			nextSelectedFilters.reduce((arr, value) => {
				if ([ADULTS, CHILDREN].includes(value?.filterName)) {
					const foundFilter = arr.findIndex((item) => [OCCUPANCY].includes(item?.filterName));
					const newValue = {
						...value,
						filterName: OCCUPANCY,
						value: { [value?.filterName]: value?.value },
					};

					if (foundFilter > -1) {
						arr[foundFilter] = {
							...arr[foundFilter],
							label: `${arr[foundFilter]?.label} , ${value?.label}`,
							filterName: OCCUPANCY,
							value: { ...arr[foundFilter]?.value, [value?.filterName]: value?.value },
						};

						return arr;
					}

					arr.push(newValue);

					return arr;
				}

				arr.push(value);

				return arr;
			}, []),
		);

		setSelectedFilters(newFilters);
		setIsTargetedPromo(qualifiersApplied.some((item) => item.includes(COUPON)));
	}, [searchApplied, filterOptionsDictionary, qualifiersApplied, uniqueDestinations, promotions, promotionApplied, locale]);

	const removePromoWithoutSearchFilters = () => {
		setPromotionSelected(null);
		setPromotionApplied(null);
		updateAdditionalFilters(null, applyFilters);
	};

	const removeFilter = (filterName, value, applyFilters, promo) => {
		const isUniqueDestination = includes(uniqueDestinationsAvailable, filterName);

		if (!isUniqueDestination && promotionApplied && promo) {
			removePromoWithoutSearchFilters();
			return;
		}

		if (isUniqueDestination) {
			const query = valueParamsToObject(value);
			const {
				[START_DATE]: startDate,
				[SHIP]: ship,
				[DESTINATION]: destination,
				[DEPARTURE_PORT]: departurePort,
				[PORT]: port,
				[NIGHTS]: nights,
			} = query;
			const filterValue = getQueryFromObject({ startDate, ship, destination, departurePort, port, nights }, '|', ':');
			const filters = getObjectFromQuery(filterValue, '|', ':') || {};
			const isPromo = value.includes(PROMO);

			if (!Object.keys(filters || {})?.length && isPromo) {
				removePromoWithoutSearchFilters();
				return;
			} else {
				_each(filters, (valueFilter, key) => {
					const filterValues = valueFilter.split(',');
					_each(filterValues, (value) => {
						removeFromSearchSelectingFilter(key, value, applyFilters, filterName, isPromo);
					});
				});
			}
		} else {
			removeFromSearchSelectingFilter(filterName, value, applyFilters);
		}

		if (filterName === START_DATE) {
			const dates = monthsSelecting?.[START_DATE]?.filter((item) => item !== value);
			updateMonthsSelectingFilter(START_DATE, dates);
			updateDatesSelectingFilter(START_DATE, dates);
		}
	};

	if (!show) return null;

	const validateRemove = () => {
		resetSoldOut();
		resetAllFilters();
		if (qualifiersApplied.some((item) => COUPON.includes(item))) {
			removeTargetedOfferApplied();
		}
	};

	const showClearButton = () => {
		return isQualifier() || selectedFilters.filter((item) => item.filterName !== QUALIFIERS)?.length;
	};

	const isQualifier = () => {
		const isTargetedApplied = !!Object.keys(targetedOfferApplied).length;
		return isTargetedPromo && ((isTargetedApplied && targetedOffersLoaded) || !isLoyaltyOrEmail);
	};

	return (
		<SelectedFiltersWrapper className={selectingForCompare ? 'compare' : ''}>
			<Wrapper id={SELECTED_FILTERS} ref={passedRef}>
				{selectedFilters.map(({ label, filterName, value, promo }) => {
					const qualifier = filterName === QUALIFIERS;
					let showPill = true;

					if (qualifier) {
						showPill = isQualifier();
					}
					const onClick = () => {
						resetSoldOut();
						const storeNumberOfGuests = localStorage.getItem(NUMBER_OF_GUESTS.key);

						if (qualifier) {
							removeFromQualifiersSelecting(value, applyFilters);
							if (includes(value, COUPON)) {
								removeTargetedOfferApplied();
							}
							return;
						}

						if (filterName === OCCUPANCY) {
							forEach(value, (item, key) => {
								removeFilter(
									key,
									item,
									() => {
										applyFilters();
										if (storeNumberOfGuests) {
											localStorage.removeItem(NUMBER_OF_GUESTS.key);
										}
									},
									promo,
								);
							});
							return;
						}

						removeFilter(filterName, value, applyFilters, promo);
					};

					if (showPill) {
						return (
							<PillButton
								dataTestId={`pill-selected-filters-button-${value}`}
								className={SELECTED_FILTER}
								key={label}
								onClick={onClick}
								selected
								showX
							>
								{label}
							</PillButton>
						);
					}
				})}
			</Wrapper>
			<ClearAllWrapper>
				{showClearButton() && (
					<ResetButton data-testid="clear-all-selected-filters-button" id={RESET_SELECTED_FILTERS} onClick={validateRemove}>
						{actionClearAll}
					</ResetButton>
				)}
			</ClearAllWrapper>
		</SelectedFiltersWrapper>
	);
}

SelectedFilters.propTypes = {
	passedRef: PropTypes.object,
};

export default SelectedFilters;
